import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { LinkTo } from '@ember/routing';
import { on } from '@ember/modifier';
import { VStack, Section } from 'tio-ui/components/layout';
import { Heading, Text } from 'tio-ui/components/typography';
import { Button } from 'tio-ui/components/buttons';
import { t, type IntlService } from 'ember-intl';
import {
  PhSparkle,
  PhArrowRight,
  PhCaretDown,
  PhCaretUp,
  PhRocket,
  PhArchive,
} from 'ember-phosphor-icons';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import StatePill from 'tio-employee/components/reimagined/tas/state-pill';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type {
  DataSourceInfo,
  LiveTasApplication,
  LiveTasData,
} from 'tio-employee/services/reimagined-data';

interface TasApplicationsModel {
  tasData: LiveTasData;
  dataSourceInfo: DataSourceInfo;
}

interface TasApplicationsSignature {
  Args: {
    model: TasApplicationsModel;
  };
}

const TERMINAL_STATES = [
  'TAS.ApplicationState.FULFILLED',
  'TAS.ApplicationState.ABANDONED',
  'TAS.ApplicationState.REJECTED_PER_POLICY',
  'TAS.ApplicationState.COURSES_REJECTED',
  'TAS.ApplicationState.EVIDENCE_REJECTED',
];

const ACTION_NEEDED_STATES = [
  'TAS.ApplicationState.DEFINE_COURSES',
  'TAS.ApplicationState.CREATED',
  'TAS.ApplicationState.COURSES_NOT_APPROVED',
  'TAS.ApplicationState.EVIDENCE_NOT_APPROVED',
  'TAS.ApplicationState.COURSES_APPROVED',
  'TAS.ApplicationState.ATTEND',
];

export default class CareerPathwaysTasApplicationsIndex extends Component<TasApplicationsSignature> {
  @service declare intl: IntlService;
  @service declare reimaginedData: ReimaginedDataService;

  @tracked showHistory = false;

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get tasSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.args.model.tasData.isLiveData ? 'live' : 'placeholder';
  }

  get applications(): LiveTasApplication[] {
    return this.args.model.tasData.applications;
  }

  get activeApplications() {
    return this.applications.filter((app) => !TERMINAL_STATES.includes(app.state));
  }

  get historyApplications() {
    return this.applications.filter((app) => TERMINAL_STATES.includes(app.state));
  }

  get hasActive() {
    return this.activeApplications.length > 0;
  }

  get hasHistory() {
    return this.historyApplications.length > 0;
  }

  formatCurrency = (value: number) =>
    this.intl.formatNumber(value, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    });

  toggleHistory = () => {
    this.showHistory = !this.showHistory;
  };

  isActionNeeded = (state: string) => ACTION_NEEDED_STATES.includes(state);

  <template>
    <VStack class="gap-6">
      {{! Hero banner }}
      <div
        class="rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-500 to-fuchsia-500 p-6 text-white shadow-lg"
      >
        <div class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div class="max-w-2xl">
            <div
              class="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-white/80"
            >
              <PhSparkle class="h-4 w-4" />
              {{t "tuition_assistance.reimagined.applications.hero_eyebrow"}}
            </div>
            <Heading @level={{1}} class="text-white">
              {{t "tuition_assistance.reimagined.applications.hero_title"}}
            </Heading>
            <Text class="text-white/80">
              {{t "tuition_assistance.reimagined.applications.hero_subtitle"}}
            </Text>
          </div>
          <LinkTo @route="authenticated.tas.dashboard">
            <Button @intent="primary">{{t
                "tuition_assistance.reimagined.applications.cta_start"
              }}</Button>
          </LinkTo>
        </div>
      </div>

      <DataIndicator
        @source={{this.tasSource}}
        @showBadge={{true}}
        @dataSourceId="tas-applications"
      >
        {{! Active Applications Section }}
        <Section class="mb-6">
          <:header>
            <div class="flex items-center gap-2">
              <PhRocket @size={{20}} class="text-indigo-600" />
              <Heading @level={{2}}>
                {{t "tuition_assistance.reimagined.applications_index.section_active"}}
              </Heading>
              {{#if this.hasActive}}
                <span
                  class="ml-2 rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-semibold text-indigo-700 dark:bg-indigo-900 dark:text-indigo-200"
                >
                  {{this.activeApplications.length}}
                </span>
              {{/if}}
            </div>
          </:header>
          <:body>
            {{#if this.hasActive}}
              <div class="grid grid-cols-1 gap-4">
                {{#each this.activeApplications as |application|}}
                  <div
                    class="rounded-2xl border bg-white p-5 shadow-sm transition-shadow hover:shadow-md dark:border-gray-700 dark:bg-gray-900
                      {{if
                        (this.isActionNeeded application.state)
                        'border-indigo-200 dark:border-indigo-800'
                        'border-gray-200'
                      }}"
                  >
                    <div class="flex items-start justify-between gap-4">
                      <div class="min-w-0">
                        <Heading @level={{3}}>{{application.name}}</Heading>
                        <Text @intent="muted">{{application.programName}}</Text>
                        {{#if application.coursesBeginDate}}
                          <Text @intent="muted" class="text-xs">
                            {{t
                              "tuition_assistance.reimagined.applications.label_starts"
                              date=application.coursesBeginDate
                            }}
                          </Text>
                        {{/if}}
                      </div>
                      <div class="flex flex-wrap items-center gap-2">
                        <StatePill @state={{application.state}} />
                        <StatePill @state={{application.paymentState}} />
                      </div>
                    </div>
                    <div class="mt-4 flex items-center justify-between">
                      <Text @intent="muted" class="text-sm">
                        {{t
                          "tuition_assistance.reimagined.applications.label_approved"
                          amount=(this.formatCurrency application.approvedTotal)
                        }}
                      </Text>
                      <LinkTo
                        @route="authenticated.reimagined.tas.applications.show"
                        @model={{application.id}}
                        class="inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
                      >
                        {{t "tuition_assistance.reimagined.applications.link_review"}}
                        <PhArrowRight class="h-4 w-4" />
                      </LinkTo>
                    </div>
                  </div>
                {{/each}}
              </div>
            {{else}}
              <div
                class="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center dark:border-gray-600 dark:bg-gray-800"
              >
                <PhRocket @size={{32}} class="mx-auto mb-3 text-gray-400" />
                <Heading @level={{3}}>
                  {{t "tuition_assistance.reimagined.applications_index.active_empty_title"}}
                </Heading>
                <Text @intent="muted" class="mt-1">
                  {{t "tuition_assistance.reimagined.applications_index.active_empty_body"}}
                </Text>
                <div class="mt-4">
                  <LinkTo @route="authenticated.tas.dashboard">
                    <Button @intent="primary">
                      {{t "tuition_assistance.reimagined.applications.cta_start"}}
                    </Button>
                  </LinkTo>
                </div>
              </div>
            {{/if}}
          </:body>
        </Section>

        {{! History Section }}
        {{#if this.hasHistory}}
          <Section>
            <:header>
              <button
                type="button"
                class="flex w-full items-center gap-2 text-left"
                {{on "click" this.toggleHistory}}
              >
                <PhArchive @size={{20}} class="text-gray-500" />
                <Heading @level={{2}}>
                  {{t "tuition_assistance.reimagined.applications_index.section_history"}}
                </Heading>
                <span
                  class="ml-2 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                >
                  {{this.historyApplications.length}}
                </span>
                <span class="ml-auto">
                  {{#if this.showHistory}}
                    <PhCaretUp @size={{16}} class="text-gray-500" />
                  {{else}}
                    <PhCaretDown @size={{16}} class="text-gray-500" />
                  {{/if}}
                </span>
              </button>
            </:header>
            <:body>
              {{#if this.showHistory}}
                <div class="grid grid-cols-1 gap-4">
                  {{#each this.historyApplications as |application|}}
                    <div
                      class="rounded-2xl border border-gray-200 bg-gray-50/50 p-5 dark:border-gray-700 dark:bg-gray-800/50"
                    >
                      <div class="flex items-start justify-between gap-4">
                        <div class="min-w-0">
                          <Heading @level={{3}}>{{application.name}}</Heading>
                          <Text @intent="muted">{{application.programName}}</Text>
                        </div>
                        <div class="flex items-center gap-2">
                          <StatePill @state={{application.state}} />
                          <StatePill @state={{application.paymentState}} />
                        </div>
                      </div>
                      <div class="mt-4 flex items-center justify-between">
                        <Text @intent="muted" class="text-sm">
                          {{t
                            "tuition_assistance.reimagined.applications.label_approved"
                            amount=(this.formatCurrency application.approvedTotal)
                          }}
                        </Text>
                        <LinkTo
                          @route="authenticated.reimagined.tas.applications.show"
                          @model={{application.id}}
                          class="inline-flex items-center gap-1 text-sm font-semibold text-gray-600 hover:text-gray-800 dark:text-gray-400"
                        >
                          {{t "tuition_assistance.reimagined.applications.link_review"}}
                          <PhArrowRight class="h-4 w-4" />
                        </LinkTo>
                      </div>
                    </div>
                  {{/each}}
                </div>
              {{/if}}
            </:body>
          </Section>
        {{/if}}

        {{! Fallback empty state when no apps at all }}
        {{#unless this.applications.length}}
          <div
            class="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center dark:border-gray-600 dark:bg-gray-800"
          >
            <Heading @level={{3}}>
              {{t "tuition_assistance.reimagined.applications.empty_title"}}
            </Heading>
            <Text @intent="muted" class="mt-1">
              {{t "tuition_assistance.reimagined.applications.empty_body"}}
            </Text>
            <div class="mt-4">
              <LinkTo @route="authenticated.tas.dashboard">
                <Button @intent="primary">
                  {{t "tuition_assistance.reimagined.applications.empty_cta"}}
                </Button>
              </LinkTo>
            </div>
          </div>
        {{/unless}}
      </DataIndicator>
    </VStack>
  </template>
}
